/**
 * Client mirror of services/event-service/src/utils/submissionEditPolicy.js
 */

export const SCREENING_PRICING_LOCK_MESSAGE =
  'Industry, category, and nominee count are locked during screening. You can update other details requested by the organiser.';

export const STAFF_CORRECTION_MESSAGE =
  'Staff correction: you can fix applicant/company names and category during judging. Category/fee changes recalculate pricing; issued invoices reverse-repost invoice/payment GL, and unpaid increases without a tax invoice may still need an additional proforma. Mismatched schedule seats are cleared so Ops can reseat the applicant.';

export function isSubmissionScreeningHold(submission) {
  const phase1 = String(submission?.screeningPhase1?.status || '').toUpperCase();
  const phase2 = String(submission?.screeningPhase2?.status || '').toUpperCase();
  return phase1 === 'HOLD' || phase2 === 'HOLD';
}

/** Payment-complete application still in Screening Phase 1 and/or Phase 2 (not finished or rejected). */
export function isSubmissionInActiveScreening(submission) {
  const statusUpper = String(submission?.status || '').toUpperCase();
  if (!['SUBMITTED', 'REVIEWED', 'ACCEPTED'].includes(statusUpper)) return false;

  const p1 = String(submission?.screeningPhase1?.status || 'PENDING').toUpperCase();
  const p2 = String(submission?.screeningPhase2?.status || 'PENDING').toUpperCase();

  if (p1 === 'REJECTED' || p2 === 'REJECTED') return false;
  if (p2 === 'APPROVED') return false;

  return true;
}

export const PROFORMA_CANCEL_MESSAGE =
  'This will cancel the entire proforma request and reset all other linked applications to Pending.';

function withScreeningPricingLocks(submission, policy) {
  if (!policy?.canEdit || !isSubmissionInActiveScreening(submission)) return policy;
  if (policy.pricingFieldsLocked && policy.lockPeopleStructure) return policy;
  if (policy.editMode === 'staff_correction') return policy;

  return {
    ...policy,
    pricingFieldsLocked: true,
    lockPeopleStructure: true,
    pricingChangeRequiresProformaCancel: false,
    message: SCREENING_PRICING_LOCK_MESSAGE,
    code: 'SUBMISSION_SCREENING_PRICING_LOCKED',
  };
}

function buildStaffCorrectionPolicy() {
  return {
    canEdit: true,
    canDelete: false,
    pricingFieldsLocked: false,
    lockPeopleStructure: true,
    editMode: 'staff_correction',
    allowCompanyChange: true,
    pricingChangeRequiresProformaCancel: false,
    pricingChangeRequiresFinanceAck: true,
    deleteRequiresProformaCancel: false,
    proformaCancelMessage: '',
    message: STAFF_CORRECTION_MESSAGE,
    code: 'SUBMISSION_STAFF_CORRECTION',
  };
}

export function getSubmissionEditPolicy(submission, {
  financiallyLocked = false,
  unpaidProformaLinked = false,
  staffChannel = false,
} = {}) {
  const statusUpper = String(submission?.status || '').toUpperCase();
  const inHold = isSubmissionScreeningHold(submission);
  const hasProformaLink = Boolean(submission?.proformaInvoiceRequest);

  if (staffChannel && (
    financiallyLocked
    || ['SUBMITTED', 'REVIEWED', 'ACCEPTED', 'TAX_INVOICE_ISSUED'].includes(statusUpper)
  )) {
    return buildStaffCorrectionPolicy();
  }

  if (inHold && ['SUBMITTED', 'REVIEWED', 'ACCEPTED'].includes(statusUpper)) {
    return withScreeningPricingLocks(submission, {
      canEdit: true,
      canDelete: false,
      pricingFieldsLocked: true,
      lockPeopleStructure: true,
      editMode: 'limited',
      pricingChangeRequiresProformaCancel: false,
      deleteRequiresProformaCancel: false,
      proformaCancelMessage: '',
      message: SCREENING_PRICING_LOCK_MESSAGE,
      code: 'SUBMISSION_SCREENING_PRICING_LOCKED',
    });
  }

  if (financiallyLocked) {
    return {
      canEdit: false,
      canDelete: false,
      pricingFieldsLocked: true,
      lockPeopleStructure: true,
      editMode: 'none',
      pricingChangeRequiresProformaCancel: false,
      deleteRequiresProformaCancel: false,
      proformaCancelMessage: '',
      message: 'This application is locked because payment was approved or a tax invoice was issued.',
      code: 'SUBMISSION_FINANCIALLY_LOCKED',
    };
  }

  if (statusUpper === 'PENDING' && !hasProformaLink) {
    return withScreeningPricingLocks(submission, {
      canEdit: true,
      canDelete: true,
      pricingFieldsLocked: false,
      lockPeopleStructure: false,
      editMode: 'full',
      pricingChangeRequiresProformaCancel: false,
      deleteRequiresProformaCancel: false,
      proformaCancelMessage: '',
    });
  }

  if (unpaidProformaLinked) {
    return withScreeningPricingLocks(submission, {
      canEdit: true,
      canDelete: true,
      pricingFieldsLocked: false,
      lockPeopleStructure: false,
      editMode: 'limited',
      pricingChangeRequiresProformaCancel: true,
      deleteRequiresProformaCancel: true,
      proformaCancelMessage: PROFORMA_CANCEL_MESSAGE,
      message:
        'You can update non-pricing details freely. Changing industry, category, nominees, or deleting this application will cancel the proforma and reset sibling applications to Pending.',
      code: 'SUBMISSION_UNPAID_PROFORMA',
    });
  }

  if (
    hasProformaLink &&
    ['PROFORMA_REQUESTED', 'PROFORMA_ISSUED'].includes(statusUpper)
  ) {
    return withScreeningPricingLocks(submission, {
      canEdit: true,
      canDelete: true,
      pricingFieldsLocked: false,
      lockPeopleStructure: false,
      editMode: 'full',
      pricingChangeRequiresProformaCancel: false,
      deleteRequiresProformaCancel: false,
      proformaCancelMessage: '',
      message: 'This application is no longer on an active proforma request. You can edit all fields.',
      code: 'SUBMISSION_STALE_PROFORMA_LINK',
    });
  }

  return withScreeningPricingLocks(submission, {
    canEdit: false,
    canDelete: false,
    pricingFieldsLocked: true,
    lockPeopleStructure: true,
    editMode: 'none',
    pricingChangeRequiresProformaCancel: false,
    deleteRequiresProformaCancel: false,
    proformaCancelMessage: '',
    message: 'This application cannot be modified in its current state.',
    code: 'SUBMISSION_NOT_EDITABLE',
  });
}

export function resolveSubmissionFinanciallyLocked(submission) {
  if (submission?.proformaLocked === true) return true;
  return String(submission?.status || '').toUpperCase() === 'TAX_INVOICE_ISSUED';
}

export function resolveSubmissionUnpaidProformaLinked(submission) {
  if (submission?.editPolicy?.pricingChangeRequiresProformaCancel) return true;
  if (submission?.unpaidProformaLinked === true) return true;
  if (resolveSubmissionFinanciallyLocked(submission)) return false;
  if (!submission?.proformaInvoiceRequest) return false;
  const statusUpper = String(submission?.status || '').toUpperCase();
  if (['PROFORMA_REQUESTED', 'PROFORMA_ISSUED', 'PENDING'].includes(statusUpper)) return true;
  if (
    submission?.screeningHoldRevision?.requiresProforma === true &&
    ['SUBMITTED', 'REVIEWED', 'ACCEPTED'].includes(statusUpper)
  ) {
    return true;
  }
  return false;
}

export function resolveSubmissionEditPolicy(submission, { staffChannel = false } = {}) {
  return getSubmissionEditPolicy(submission, {
    financiallyLocked: resolveSubmissionFinanciallyLocked(submission),
    unpaidProformaLinked: resolveSubmissionUnpaidProformaLinked(submission),
    staffChannel,
  });
}

function v2CategorySelectionFingerprint(people) {
  return (Array.isArray(people) ? people : [])
    .map((p) => String(p?.categoryId || '').trim())
    .join('\u0001');
}

export function pricingAssignmentChanged(submission, { industryId, categoryId, people, v2Mode }) {
  if (v2Mode) {
    const oldFp = v2CategorySelectionFingerprint(submission?.people);
    const newFp = v2CategorySelectionFingerprint(people);
    const oldHead = submission?.category ? String(submission.category) : '';
    if (String(categoryId || '').trim() !== String(oldHead || '').trim()) return true;
    return oldFp !== newFp;
  }
  const oldInd = submission?.industry ? String(submission.industry) : '';
  const oldCat = submission?.category ? String(submission.category) : '';
  if (String(industryId || '').trim() !== String(oldInd || '').trim()) return true;
  if (String(categoryId || '').trim() !== String(oldCat || '').trim()) return true;
  return false;
}

export function peopleCountChanged(submission, people) {
  const oldLen = Array.isArray(submission?.people) ? submission.people.length : 0;
  const newLen = Array.isArray(people) ? people.length : 0;
  return oldLen !== newLen;
}

export function submissionPricingWillChange(submission, form, { industryId, categoryId, people }) {
  const v2Mode = Number(form?.builderVersion || 1) === 2;
  return pricingAssignmentChanged(submission, { industryId, categoryId, people, v2Mode })
    || peopleCountChanged(submission, people);
}

/** Snapshot used to detect pricing-field edits (category, industry, nominee count). */
export function buildPricingBaselineFromSubmission(submission) {
  if (!submission) return null;
  const categoryId = submission.category?._id || submission.category || submission.categoryId || '';
  const industryId = submission.industry?._id || submission.industry || submission.industryId || '';
  return {
    industry: industryId ? String(industryId) : '',
    category: categoryId ? String(categoryId) : '',
    people: Array.isArray(submission.people)
      ? submission.people.map((person) => ({
        categoryId: String(
          person?.categoryId || categoryId || ''
        ).trim(),
      }))
      : [],
  };
}
